import React, { useState } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { useAuth } from '../contexts/AuthContext';
import { createCrmOrg } from '../services/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Buildings, CaretUpDown, CheckCircle, Plus, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function OrgSelector() {
  const { orgs, currentOrg, switchOrg, refreshOrgs } = useOrg();
  const { user } = useAuth();
  const [createDialog, setCreateDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#2D5A4C');
  const [creating, setCreating] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  if (!currentOrg) return null;

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await createCrmOrg({ name: newName.trim(), color: newColor });
      toast.success('Organisation creee');
      setCreateDialog(false);
      setNewName('');
      await refreshOrgs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur creation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-[#F9F8F5] transition-colors border border-transparent hover:border-[#E8E6E1] max-w-[220px]"
            data-testid="org-selector-trigger"
          >
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
              style={{ backgroundColor: currentOrg.color || '#2D5A4C' }}
            >
              {currentOrg.name?.charAt(0) || 'O'}
            </div>
            <span className="text-xs font-medium text-[#1A2421] truncate hidden xl:inline">
              {currentOrg.name}
            </span>
            <CaretUpDown size={12} className="text-[#5E6D66] flex-shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[260px]" data-testid="org-selector-menu">
          <div className="px-3 py-2">
            <p className="text-[10px] font-semibold text-[#5E6D66] uppercase tracking-wider">Organisations</p>
          </div>
          {orgs.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => { if (org.id !== currentOrg.id) switchOrg(org.id); }}
              className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
              data-testid={`org-option-${org.id}`}
            >
              <div
                className="w-5 h-5 rounded flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                style={{ backgroundColor: org.color || '#2D5A4C' }}
              >
                {org.name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[#1A2421] truncate">{org.name}</p>
                <p className="text-[10px] text-[#5E6D66]">{org.user_role}</p>
              </div>
              {org.id === currentOrg.id && (
                <CheckCircle size={14} className="text-[#2D5A4C] flex-shrink-0" weight="fill" />
              )}
            </DropdownMenuItem>
          ))}

          {orgs.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => window.location.href = '/dashboard?view=consolidated'}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                data-testid="org-consolidated-view"
              >
                <Globe size={14} className="text-[#5E6D66]" />
                <span className="text-xs text-[#5E6D66]">Vue consolidee</span>
              </DropdownMenuItem>
            </>
          )}

          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setCreateDialog(true)}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer text-[#2D5A4C]"
                data-testid="org-create-btn"
              >
                <Plus size={14} />
                <span className="text-xs font-medium">Nouvelle organisation</span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Buildings size={20} className="text-[#2D5A4C]" />
              Nouvelle organisation CRM
            </DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de l'organisation *</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Antenne Reunion, Bureau Paris..."
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border border-[#E8E6E1]"
                  data-testid="org-color-input"
                />
                <span className="text-xs text-[#5E6D66] font-mono">{newColor}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Annuler</Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="bg-[#2D5A4C] hover:bg-[#1E4136]"
              data-testid="org-create-submit"
            >
              Creer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
